import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";

import { Skeleton } from "@/components/ui/skeleton";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { PlanCard } from "@/components/plans/PlanCard";
import { PortfolioConsultationDialog } from "@/components/plans/PortfolioConsultationDialog";
import { usePlans } from "@/hooks/usePlans";
import { useAuth } from "@/hooks/useAuth";
import type { BillingCycle, PlanDto } from "@/types/api";

const SECTIONS: Array<{
  title: string;
  type: "email" | "hosting" | "portfolio";
  eyebrow: string;
  sub: string;
  highlightSlug?: string;
}> = [
  {
    title: "Email plans",
    type: "email",
    eyebrow: "Professional email",
    sub: "Deliverability-first inboxes for teams who live in their inbox.",
    highlightSlug: "email-growth"
  },
  {
    title: "Hosting plans",
    type: "hosting",
    eyebrow: "Website + email",
    sub: "Full stack — website hosting bundled with managed email.",
    highlightSlug: "hosting-growth"
  },
  {
    title: "Portfolio plans",
    type: "portfolio",
    eyebrow: "Multi-domain",
    sub: "For agencies, incubators, and portfolio operators running many brands.",
    highlightSlug: "portfolio-starter"
  }
];

export function PlansPage() {
  // Plans always show the monthly starting price here; the billing cadence is
  // chosen later in the purchase flow (term step, before domain attachment).
  const cycle: BillingCycle = "monthly";
  const { data, isLoading, error } = usePlans();
  const { status } = useAuth();

  // Optional ?type=email|hosting|portfolio focuses the page on a single
  // category (linked from the marketing home). No/unknown type → show all.
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get("type");
  const visibleSections = React.useMemo(
    () => (SECTIONS.some(s => s.type === typeParam)
      ? SECTIONS.filter(s => s.type === typeParam)
      : SECTIONS),
    [typeParam]
  );

  const [consultOpen, setConsultOpen] = React.useState(false);
  const [consultPlanSlug, setConsultPlanSlug] = React.useState<string | undefined>();

  function openConsultFor(slug: string) {
    setConsultPlanSlug(slug);
    setConsultOpen(true);
  }

  // Group plans by type, drop inactive (the API filters by default but
  // defensive code keeps the UI clean if the admin flag flips client-side).
  const grouped = React.useMemo(() => {
    const map: Record<string, PlanDto[]> = { email: [], hosting: [], portfolio: [] };
    (data ?? []).forEach(p => {
      if (!p.active) return;
      const bucket = map[p.plan_type] ?? (map[p.plan_type] = []);
      bucket.push(p);
    });
    // Sort each bucket by monthly price asc — keeps Starter / Growth / Pro
    // in the natural reading order without depending on insertion order.
    Object.values(map).forEach(list => list.sort((a, b) => a.price_monthly - b.price_monthly));
    return map;
  }, [data]);

  return (
    <div className="brand-gradient-bg brand-radial-halo relative min-h-[100dvh] overflow-hidden">
      <PublicTopBar authStatus={status} />

      <main className="relative z-10 mx-auto w-full max-w-[1240px] px-4 pb-20 pt-10 sm:px-6 sm:pt-16">
        <Header />

        {error && !isLoading && (
          <p className="mt-10 rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
            Couldn't load plans. Make sure the API is running and refresh.
          </p>
        )}

        {visibleSections.map(section => (
          <Section
            key={section.type}
            section={section}
            plans={grouped[section.type] ?? []}
            cycle={cycle}
            loading={isLoading}
            onConsultRequested={openConsultFor}
          />
        ))}

        <FaqStrip />
      </main>

      <PortfolioConsultationDialog
        open={consultOpen}
        onOpenChange={setConsultOpen}
        planSlug={consultPlanSlug}
      />
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────
function Header() {
  return (
    <div className="text-center">
      <span className="brand-pill-recommended">Pricing</span>
      <h1 className="mt-4 font-display text-3xl font-bold tracking-tight sm:text-5xl">
        Built for businesses that take email
        <br className="hidden sm:block" />{" "}
        <span className="text-brand-accent">seriously</span>
      </h1>
      <p className="mx-auto mt-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
        Same infrastructure that powers Fortune 500 deliverability — packaged for businesses
        running on Promonu and GoHighLevel.
      </p>

      <div className="mt-7 flex flex-col items-center gap-3 sm:mt-9">
        <p className="text-xs text-muted-foreground">
          Prices shown monthly · Switch anytime · Cancel anytime · No setup fees
        </p>
        <Link
          to="/onboarding"
          className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand-border bg-background px-3 py-1.5 text-xs font-medium text-brand-accentDark transition-colors hover:bg-brand-accentSoft"
        >
          Not sure which plan?  Take the 30-second quiz →
        </Link>
      </div>
    </div>
  );
}

// ── Section ──────────────────────────────────────────────────
function Section({
  section,
  plans,
  cycle,
  loading,
  onConsultRequested
}: {
  section: (typeof SECTIONS)[number];
  plans: PlanDto[];
  cycle: BillingCycle;
  loading: boolean;
  onConsultRequested: (slug: string) => void;
}) {
  return (
    <section className="mt-16 sm:mt-24">
      <div className="mx-auto max-w-2xl text-center">
        <p className="font-mono text-xs font-bold uppercase tracking-[0.18em] text-brand-accentDark">
          {section.eyebrow}
        </p>
        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">
          {section.title}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground sm:text-base">{section.sub}</p>
      </div>

      <div className="mt-8 grid gap-5 sm:mt-10 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <PlanSkeleton key={i} />)
          : plans.length === 0
            ? (
                <p className="md:col-span-2 lg:col-span-3 text-center text-sm text-muted-foreground">
                  No plans in this section yet.
                </p>
              )
            : plans.map(plan => (
                <PlanCard
                  key={plan.slug}
                  plan={plan}
                  cycle={cycle}
                  highlighted={plan.slug === section.highlightSlug}
                  onConsultRequested={() => onConsultRequested(plan.slug)}
                />
              ))}
      </div>
    </section>
  );
}

function PlanSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-7 shadow-brand-sm">
      <Skeleton className="h-3 w-16" />
      <Skeleton className="mt-2 h-7 w-32" />
      <Skeleton className="mt-2 h-4 w-48" />
      <Skeleton className="mt-5 h-12 w-32" />
      <div className="mt-6 space-y-2.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
      <Skeleton className="mt-8 h-11 w-full" />
    </div>
  );
}

// ── Public top bar ──────────────────────────────────────────
function PublicTopBar({ authStatus }: { authStatus: string }) {
  return (
    <header className="relative z-20 mx-auto flex max-w-[1240px] items-center justify-between gap-3 px-4 py-4 sm:px-6 sm:py-5">
      <Link to="/" className="flex items-center">
        <BrandLogo className="h-8 w-auto" />
      </Link>

      <nav className="flex items-center gap-1 text-sm sm:gap-2">
        {authStatus === "authenticated" ? (
          <Link
            to="/dashboard"
            className="rounded-md px-3 py-2 font-medium text-foreground hover:bg-muted"
          >
            Dashboard
          </Link>
        ) : (
          <>
            <Link
              to="/login"
              className="rounded-md px-3 py-2 font-medium text-foreground hover:bg-muted"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="rounded-md bg-primary px-3 py-2 font-medium text-primary-foreground shadow-brand-sm hover:bg-primary/90"
            >
              Get started
            </Link>
          </>
        )}
      </nav>
    </header>
  );
}

// ── Trust strip below the cards ─────────────────────────────
function FaqStrip() {
  return (
    <section className="mt-20 grid gap-6 rounded-2xl border border-border bg-background p-6 sm:mt-28 sm:grid-cols-3 sm:p-10">
      <Fact title="No setup fees" body="Pick a plan, finish checkout, you're in. Provisioning is automatic." />
      <Fact title="Switch anytime" body="Change cadence or upgrade tiers without losing your mailboxes." />
      <Fact title="20i + AWS SES" body="Deliverability you'd otherwise piece together yourself — managed." />
    </section>
  );
}

function Fact({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <p className="font-display text-lg font-semibold">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
